<template>
  <Teleport to="body">
    <div
      v-if="modelValue && confidence"
      class="confidence-modal-backdrop"
      role="presentation"
      @click.self="close"
    >
      <section
        class="confidence-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduling-confidence-title"
        tabindex="-1"
        ref="dialog"
        @keydown.esc="close"
      >
        <header class="confidence-modal-header">
          <div>
            <p class="confidence-modal-eyebrow">Scheduling Confidence Details</p>
            <h2 id="scheduling-confidence-title" class="confidence-modal-title">
              {{ personLabel }}
            </h2>
            <p v-if="confidence.person_id !== null && confidence.person_id !== undefined" class="confidence-modal-subtitle">
              Person ID: {{ confidence.person_id }}
            </p>
          </div>
          <button
            type="button"
            class="confidence-close-button"
            aria-label="Close scheduling confidence details"
            @click="close"
          >
            <i class="material-icons" aria-hidden="true">close</i>
          </button>
        </header>

        <div class="confidence-modal-body">
          <div class="confidence-overview">
            <div class="confidence-score" :class="scoreClass">
              <span class="confidence-score-value">{{ scoreDisplay }}</span>
              <span class="confidence-score-label">confidence</span>
            </div>
            <div class="confidence-overview-copy">
              <h3>Why this person was scheduled</h3>
              <p>
                This score summarizes the scheduling criteria and the adjustments made by
                the scheduling rules. A rule that is not passed may represent a fairness
                penalty or other score adjustment; it does not necessarily mean the person
                was ineligible.
              </p>
            </div>
          </div>

          <div v-if="criteria.length" class="confidence-section">
            <div class="confidence-section-heading">
              <h3>Scheduling criteria</h3>
              <span>{{ passedCriteriaCount }} of {{ criteria.length }} passed</span>
            </div>
            <div class="criteria-list">
              <div v-for="criterion in criteria" :key="criterion.key || criterion.label" class="criterion-row">
                <i
                  class="material-icons criterion-status"
                  :class="criterion.passed ? 'status-passed' : 'status-not-passed'"
                  aria-hidden="true"
                >
                  {{ criterion.passed ? 'check_circle' : 'cancel' }}
                </i>
                <div class="criterion-copy">
                  <strong>{{ criterion.label || criterion.key || 'Criterion' }}</strong>
                  <span>
                    {{ criterion.passed ? 'Passed' : 'Not passed' }}
                    <template v-if="criterion.weight !== null">
                      · Weight {{ criterion.weight }}%
                    </template>
                  </span>
                </div>
                <span class="criterion-contribution">
                  {{ criterion.passed && criterion.weight !== null ? `+${criterion.weight}` : '0' }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="rules.length" class="confidence-section">
            <div class="confidence-section-heading">
              <h3>Scheduling rule decisions</h3>
              <span>{{ rules.length }} evaluated {{ rules.length === 1 ? 'rule' : 'rules' }}</span>
            </div>
            <p class="confidence-section-intro">
              Rules are shown in evaluation order. The score change shows how each rule affected
              the scheduling score; a negative value is a penalty and a positive value is a bonus.
            </p>
            <div class="rule-list">
              <article v-for="(rule, index) in rules" :key="rule.rule_id || `${rule.rule_name}-${index}`" class="rule-card">
                <div class="rule-card-header">
                  <div class="rule-title-wrap">
                    <span class="rule-number">{{ index + 1 }}</span>
                    <div>
                      <h4>{{ rule.rule_name || rule.rule_class || 'Scheduling rule' }}</h4>
                      <span class="rule-class">{{ rule.rule_class || 'Rule evaluation' }}</span>
                    </div>
                  </div>
                  <span class="rule-status" :class="ruleStatusClass(rule)">
                    {{ ruleStatus(rule) }}
                  </span>
                </div>

                <div class="rule-metrics">
                  <span v-if="rule.score_before !== null">
                    Before <strong>{{ formatScore(rule.score_before) }}</strong>
                  </span>
                  <span v-if="rule.score_after !== null">
                    After <strong>{{ formatScore(rule.score_after) }}</strong>
                  </span>
                  <span v-if="rule.score_change !== null" :class="scoreChangeClass(rule.score_change)">
                    Change <strong>{{ formatSignedScore(rule.score_change) }}</strong>
                  </span>
                </div>

                <p v-if="rule.context_detail" class="rule-context">{{ rule.context_detail }}</p>
                <pre v-if="rule.exec_summary" class="rule-summary">{{ rule.exec_summary }}</pre>
                <p v-if="!rule.context_detail && !rule.exec_summary" class="rule-no-detail">
                  No additional explanation was returned for this rule.
                </p>
              </article>
            </div>
          </div>

          <div v-if="!criteria.length && !rules.length" class="confidence-empty-state">
            <i class="material-icons" aria-hidden="true">info</i>
            <p>No criterion or rule details were returned for this scheduled person.</p>
          </div>

          <dl class="confidence-metadata">
            <div v-if="confidence.version !== null && confidence.version !== undefined">
              <dt>Scoring version</dt>
              <dd>{{ confidence.version }}</dd>
            </div>
            <div v-if="confidence.location_id !== null && confidence.location_id !== undefined">
              <dt>Location ID</dt>
              <dd>{{ confidence.location_id }}</dd>
            </div>
            <div v-if="confidence.activity_instance_id !== null && confidence.activity_instance_id !== undefined">
              <dt>Activity instance ID</dt>
              <dd>{{ confidence.activity_instance_id }}</dd>
            </div>
            <div v-if="confidence.activity_instance_workgroup_id !== null && confidence.activity_instance_workgroup_id !== undefined">
              <dt>Instance workgroup ID</dt>
              <dd>{{ confidence.activity_instance_workgroup_id }}</dd>
            </div>
            <div v-if="confidence.calculated_at">
              <dt>Calculated</dt>
              <dd>{{ formatDate(confidence.calculated_at) }}</dd>
            </div>
          </dl>
        </div>

        <footer class="confidence-modal-footer">
          <button type="button" class="btn btn-secondary m-0" @click="close">Close</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  confidence: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])
const dialog = ref(null)

const criteria = computed(() => {
  if (!Array.isArray(props.confidence?.criteria)) return []
  return props.confidence.criteria.map((criterion) => ({
    ...criterion,
    weight: criterion.weight === null || criterion.weight === undefined ? null : criterion.weight,
  }))
})
const rules = computed(() => Array.isArray(props.confidence?.scheduling_rules) ? props.confidence.scheduling_rules : [])
const personLabel = computed(() => {
  const confidence = props.confidence || {}
  return confidence.person_name || confidence.personName || confidence.name || confidence.email || `Person #${confidence.person_id ?? 'Unknown'}`
})
const score = computed(() => {
  const value = Number(props.confidence?.score)
  return Number.isFinite(value) ? value : null
})
const scoreDisplay = computed(() => score.value === null ? '—' : `${Math.round(score.value)}%`)
const scoreClass = computed(() => {
  if (score.value === null) return 'score-unknown'
  if (score.value >= 75) return 'score-high'
  if (score.value >= 50) return 'score-medium'
  return 'score-low'
})
const passedCriteriaCount = computed(() => criteria.value.filter((criterion) => criterion.passed === true).length)

const close = () => emit('update:modelValue', false)

const formatScore = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number.toLocaleString() : '—'
}

const formatSignedScore = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  return `${number > 0 ? '+' : ''}${number.toLocaleString()}`
}

const formatDate = (value) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const ruleStatus = (rule) => {
  if (rule?.evaluated === false) return 'Not evaluated'
  if (rule?.eligible === false) return 'Not eligible'
  if (rule?.passed === true) return 'Passed'
  if (Number(rule?.score_change) < 0) return 'Penalty applied'
  if (Number(rule?.score_change) > 0) return 'Bonus applied'
  return 'Not passed'
}

const ruleStatusClass = (rule) => {
  if (rule?.passed === true || Number(rule?.score_change) > 0) return 'rule-status-positive'
  if (Number(rule?.score_change) < 0) return 'rule-status-negative'
  return 'rule-status-neutral'
}

const scoreChangeClass = (value) => {
  const number = Number(value)
  if (number > 0) return 'score-change-positive'
  if (number < 0) return 'score-change-negative'
  return ''
}

const handleDocumentKeydown = (event) => {
  if (event.key === 'Escape' && props.modelValue) close()
}

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleDocumentKeydown)
    await nextTick()
    dialog.value?.focus()
  } else {
    document.removeEventListener('keydown', handleDocumentKeydown)
  }
})

onBeforeUnmount(() => document.removeEventListener('keydown', handleDocumentKeydown))
</script>

<style scoped>
.confidence-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.64);
}

.confidence-modal {
  display: flex;
  flex-direction: column;
  width: min(900px, 100%);
  max-height: min(900px, calc(100vh - 40px));
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.28);
  outline: none;
}

.confidence-modal-header,
.confidence-modal-footer {
  flex-shrink: 0;
  padding: 20px 24px;
}

.confidence-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
}

.confidence-modal-eyebrow {
  margin: 0 0 4px;
  color: #4f46e5;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.confidence-modal-title {
  margin: 0;
  color: #1e293b;
  font-size: 1.3rem;
  font-weight: 700;
}

.confidence-modal-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.8rem;
}

.confidence-close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: -4px -8px 0 16px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #64748b;
  cursor: pointer;
}

.confidence-close-button:hover,
.confidence-close-button:focus-visible {
  background: #f1f5f9;
  color: #1e293b;
}

.confidence-modal-body {
  overflow-y: auto;
  padding: 24px;
}

.confidence-overview {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px;
  border: 1px solid #c7d2fe;
  border-radius: 12px;
  background: #eef2ff;
}

.confidence-score {
  display: flex;
  flex: 0 0 112px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  color: #fff;
}

.score-high { background: #15803d; }
.score-medium { background: #b45309; }
.score-low { background: #b91c1c; }
.score-unknown { background: #64748b; }

.confidence-score-value {
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.1;
}

.confidence-score-label {
  margin-top: 4px;
  font-size: 0.72rem;
  text-transform: uppercase;
}

.confidence-overview-copy h3,
.confidence-section-heading h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 700;
}

.confidence-overview-copy p,
.confidence-section-intro {
  margin: 6px 0 0;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.55;
}

.confidence-section {
  margin-top: 24px;
}

.confidence-section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8eaf6;
}

.confidence-section-heading > span {
  color: #64748b;
  font-size: 0.78rem;
}

.criteria-list,
.rule-list {
  display: grid;
  gap: 8px;
}

.criterion-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.criterion-status {
  flex-shrink: 0;
  font-size: 20px;
}

.status-passed { color: #16a34a; }
.status-not-passed { color: #dc2626; }

.criterion-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: #334155;
  font-size: 0.84rem;
}

.criterion-copy span {
  color: #64748b;
  font-size: 0.76rem;
}

.criterion-contribution {
  margin-left: auto;
  color: #334155;
  font-size: 0.82rem;
  font-weight: 700;
}

.rule-card {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #94a3b8;
  border-radius: 8px;
  background: #f8fafc;
}

.rule-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.rule-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.rule-number {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 0.72rem;
  font-weight: 700;
}

.rule-card h4 {
  margin: 0;
  color: #1e293b;
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}

.rule-class {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 0.72rem;
  overflow-wrap: anywhere;
}

.rule-status {
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.rule-status-positive { background: #dcfce7; color: #166534; }
.rule-status-negative { background: #fee2e2; color: #991b1b; }
.rule-status-neutral { background: #e2e8f0; color: #475569; }

.rule-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0 8px 34px;
  color: #64748b;
  font-size: 0.76rem;
}

.rule-metrics strong { color: #334155; }
.score-change-positive strong { color: #15803d; }
.score-change-negative strong { color: #b91c1c; }

.rule-context {
  margin: 8px 0 0 34px;
  color: #334155;
  font-size: 0.8rem;
  line-height: 1.45;
}

.rule-summary {
  max-height: 180px;
  margin: 8px 0 0 34px;
  padding: 10px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font: inherit;
  font-size: 0.76rem;
  line-height: 1.45;
  white-space: pre-wrap;
}

.rule-no-detail {
  margin: 8px 0 0 34px;
  color: #64748b;
  font-size: 0.78rem;
  font-style: italic;
}

.confidence-empty-state {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding: 14px;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.84rem;
}

.confidence-empty-state p { margin: 0; }

.confidence-metadata {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin: 24px 0 0;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.confidence-metadata div { min-width: 0; }
.confidence-metadata dt { color: #64748b; font-size: 0.72rem; }
.confidence-metadata dd { margin: 2px 0 0; color: #334155; font-size: 0.8rem; overflow-wrap: anywhere; }

.confidence-modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 600px) {
  .confidence-modal-backdrop { padding: 10px; }
  .confidence-modal { max-height: calc(100vh - 20px); }
  .confidence-modal-header,
  .confidence-modal-body,
  .confidence-modal-footer { padding: 16px; }
  .confidence-overview { align-items: flex-start; flex-direction: column; }
  .confidence-score { flex-basis: 80px; width: 80px; height: 80px; }
  .confidence-score-value { font-size: 1.25rem; }
  .rule-card-header { flex-direction: column; }
  .rule-status { align-self: flex-start; }
  .rule-metrics,
  .rule-context,
  .rule-summary,
  .rule-no-detail { margin-left: 0; }
}
</style>
